import React, { memo, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { ROUTE_KEYS } from 'legacyfrontend/constants';

const renderContent = (
  status,
  bookingId,
  errorMessage,
  background,
  text,
  onClose,
  handleRedirectToHome,
  handleBackArrow
) => {
  switch (status) {
    case 'loading':
      return (
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
            <CircularProgress
              size={80}
              sx={{
                color: background,
                mb: 2,
              }}
            />
            <Typography component='div' gutterBottom variant='h6'>
              Procesando Reserva
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: 2,
              }}
            >
              <Typography sx={{ mb: 3 }} variant='body1'>
                Estamos procesando su solicitud de reserva, por favor espere un momento.
              </Typography>
            </Box>
          </DialogContent>
        </>
      );

    case 'success':
      return (
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 80,
                color: background,
                mb: 2,
              }}
            />
            <Typography component='div' gutterBottom variant='h6'>
              ¡Reserva Exitosa!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: 2,
              }}
            >
              <Typography gutterBottom variant='h6'>
                Su reserva ha sido procesada correctamente
              </Typography>

              <Typography sx={{ mb: 3 }} variant='body1'>
                ID de reserva: <strong>{bookingId}</strong>
              </Typography>

              <Typography color='text.secondary' sx={{ mb: 3 }} variant='body2'>
                Hemos enviado los detalles de su reserva al correo electrónico registrado. Será
                redirigido a la página principal en unos segundos.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <CircularProgress
                  size={20}
                  sx={{
                    color: background,
                    mr: 1,
                  }}
                />
                <Typography color='text.secondary' variant='body2'>
                  Redirigiendo...
                </Typography>
              </Box>

              <Button
                onClick={handleRedirectToHome}
                size='large'
                sx={{
                  backgroundColor: background,
                  color: text,
                  minWidth: '215px',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: background,
                    color: text,
                    opacity: 0.7,
                  },
                }}
                variant='contained'
              >
                Ir a la página principal
              </Button>
            </Box>
          </DialogContent>
        </>
      );

    case 'error':
      return (
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
            <ErrorOutlineIcon
              sx={{
                fontSize: 80,
                color: '#f44336',
                mb: 2,
              }}
            />
            <Typography component='div' gutterBottom variant='h6'>
              Error en la Reserva
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: 2,
              }}
            >
              <Typography sx={{ mb: 3 }} variant='body1'>
                {errorMessage || 'Ha ocurrido un error al procesar su reserva.'}
              </Typography>

              <Button
                onClick={onClose}
                size='large'
                sx={{
                  backgroundColor: background,
                  color: text,
                  minWidth: '215px',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: background,
                    color: text,
                    opacity: 0.7,
                  },
                  mb: 2,
                }}
                variant='contained'
              >
                Intentar de nuevo
              </Button>

              <Button
                onClick={handleBackArrow}
                size='large'
                sx={{
                  borderColor: background,
                  color: background,
                  minWidth: '215px',
                  textDecoration: 'none',
                  '&:hover': {
                    borderColor: background,
                    color: background,
                    backgroundColor: 'transparent',
                    opacity: 0.7,
                  },
                }}
                variant='outlined'
              >
                Volver a los resultados
              </Button>
            </Box>
          </DialogContent>
        </>
      );

    default:
      return null;
  }
};

const BookingStatusModal = memo(
  ({
    open,
    bookingId,
    onClose,
    redirectDelay = 10000,
    status = 'loading',
    errorMessage = 'Ha ocurrido un error al procesar su reserva.',
    cleanSessionStorage,
    handleBackArrow,
  }) => {
    const { background, text } = useColors();

    const handleRedirectToHome = useCallback(() => {
      cleanSessionStorage();
      window.location.href = ROUTE_KEYS.HOME;
    }, [cleanSessionStorage]);

    const handleClose = useCallback(
      (event, reason) => {
        if (status === 'loading') {
          return;
        }
        onClose(event, reason);
      },
      [status, onClose]
    );

    const modalContent = useMemo(
      () =>
        renderContent(
          status,
          bookingId,
          errorMessage,
          background,
          text,
          handleClose,
          handleRedirectToHome,
          handleBackArrow
        ),
      [
        status,
        bookingId,
        errorMessage,
        background,
        text,
        handleClose,
        handleRedirectToHome,
        handleBackArrow,
      ]
    );

    useEffect(() => {
      let redirectTimer;

      if (open && status === 'success') {
        redirectTimer = setTimeout(handleRedirectToHome, redirectDelay);
      }

      return () => {
        if (redirectTimer) clearTimeout(redirectTimer);
      };
    }, [open, status, redirectDelay, handleRedirectToHome]);

    if (!open) return null;

    return (
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: '15px',
            p: 2,
          },
        }}
        disableEscapeKeyDown={status === 'loading'}
        disableScrollLock={false}
        fullWidth
        hideBackdrop={false}
        maxWidth='sm'
        onClose={handleClose}
        open={open}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        {modalContent}
      </Dialog>
    );
  }
);

export default BookingStatusModal;
